/**
 * Sample / fabricated theme KPIs for the Thematic Overview drill-down drawer.
 *
 * Each entry defines a fixed list of 5-6 realistic KPIs per theme.
 * Targets are constants; achieved values are generated deterministically per
 * (theme, kpi) using a stable seed so the numbers don't change on refresh.
 *
 * Status buckets (driven by achieved/target %):
 *   Completed ≥ 100   green
 *   On Track  75-99   emerald
 *   At Risk   50-74   amber
 *   Off Track < 50    red
 */

// String -> 32-bit positive int (xmur3-lite)
function seed32(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  h ^= h >>> 16;
  return h >>> 0;
}

function rngFrom(seed) {
  let t = seed >>> 0;
  return function () {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), t | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

// 5–6 KPI templates per theme. Units: "" = count, "%" = percent, "ha" = hectares
const TEMPLATES = {
  "Education": [
    { name: "Students enrolled in supported schools", target: 48000, unit: "" },
    { name: "Teachers trained (cumulative)", target: 2200, unit: "" },
    { name: "Schools with operational learning labs", target: 180, unit: "" },
    { name: "Average learning improvement (Grade 3-5)", target: 25, unit: "%" },
    { name: "Dropout rate reduction (vs baseline)", target: 30, unit: "%" },
    { name: "Libraries set up in partner schools", target: 90, unit: "" },
  ],
  "Health": [
    { name: "Patients screened in outreach camps", target: 120000, unit: "" },
    { name: "Health camps conducted", target: 480, unit: "" },
    { name: "Frontline health workers trained", target: 3200, unit: "" },
    { name: "OPD visits at supported facilities", target: 250000, unit: "" },
    { name: "Immunisation coverage (children <5)", target: 90, unit: "%" },
    { name: "Maternal check-ups completed (ANC-3+)", target: 18000, unit: "" },
  ],
  "Specialised Healthcare": [
    { name: "Cancer screenings completed", target: 45000, unit: "" },
    { name: "Tele-consultations delivered", target: 120000, unit: "" },
    { name: "Specialists onboarded to network", target: 220, unit: "" },
    { name: "Patients on free / subsidised treatment", target: 8500, unit: "" },
    { name: "Reduction in patient travel cost (avg)", target: 35, unit: "%" },
  ],
  "Nutrition": [
    { name: "Anganwadi centres supported", target: 1200, unit: "" },
    { name: "Children screened for SAM/MAM", target: 65000, unit: "" },
    { name: "Mothers counselled on IYCF practices", target: 28000, unit: "" },
    { name: "Reduction in stunting prevalence (0-5y)", target: 20, unit: "%" },
    { name: "Kitchen gardens set up in households", target: 9500, unit: "" },
  ],
  "WASH": [
    { name: "Households with safe drinking water", target: 35000, unit: "" },
    { name: "Toilets built / refurbished", target: 12000, unit: "" },
    { name: "Villages declared ODF-Plus", target: 280, unit: "" },
    { name: "Schools with functional WASH blocks", target: 420, unit: "" },
    { name: "Handwashing behaviour adoption", target: 75, unit: "%" },
  ],
  "Rural Development": [
    { name: "Self-Help Groups formed / strengthened", target: 2800, unit: "" },
    { name: "Farmers trained on climate-resilient practices", target: 55000, unit: "" },
    { name: "Acres under sustainable agriculture", target: 38000, unit: "ha" },
    { name: "Average household income increase", target: 40, unit: "%" },
    { name: "Producer organisations (FPOs) supported", target: 65, unit: "" },
    { name: "Watershed structures built", target: 1800, unit: "" },
  ],
  "Urban Development": [
    { name: "Slum households reached with services", target: 22000, unit: "" },
    { name: "Public spaces rehabilitated", target: 95, unit: "" },
    { name: "Urban youth placed in livelihoods", target: 14000, unit: "" },
    { name: "Waste-segregation adoption (wards)", target: 60, unit: "%" },
    { name: "Affordable housing units enabled", target: 3200, unit: "" },
  ],
  "Culture & Heritage": [
    { name: "Heritage sites conserved / restored", target: 45, unit: "" },
    { name: "Artisans receiving livelihood support", target: 6800, unit: "" },
    { name: "Cultural events / exhibitions hosted", target: 120, unit: "" },
    { name: "Visitors to supported museums", target: 480000, unit: "" },
    { name: "Traditional knowledge documents archived", target: 950, unit: "" },
  ],
  "Institutional Strengthening": [
    { name: "Partner NGOs receiving capacity-building", target: 140, unit: "" },
    { name: "Staff trained on M&E and compliance", target: 1800, unit: "" },
    { name: "Governance audits completed on time", target: 95, unit: "%" },
    { name: "Partners with improved finance ratings", target: 80, unit: "%" },
    { name: "Joint policy briefs published", target: 24, unit: "" },
  ],
  "Institutions": [
    { name: "Partner NGOs receiving capacity-building", target: 140, unit: "" },
    { name: "Staff trained on M&E and compliance", target: 1800, unit: "" },
    { name: "Governance audits completed on time", target: 95, unit: "%" },
    { name: "Partners with improved finance ratings", target: 80, unit: "%" },
    { name: "Joint policy briefs published", target: 24, unit: "" },
  ],
  "Others": [
    { name: "Cross-cutting beneficiaries reached", target: 18000, unit: "" },
    { name: "Innovation pilots supported", target: 35, unit: "" },
    { name: "Knowledge products published", target: 60, unit: "" },
    { name: "Volunteers mobilised", target: 4200, unit: "" },
    { name: "Convenings / workshops hosted", target: 85, unit: "" },
  ],
};

const FALLBACK = TEMPLATES["Others"];

export function statusFor(pct) {
  if (pct >= 100) return { key: "completed", label: "Completed", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", bar: "#059669" };
  if (pct >= 75) return { key: "on_track", label: "On Track", color: "text-green-700", bg: "bg-green-50", border: "border-green-200", bar: "#16A34A" };
  if (pct >= 50) return { key: "at_risk", label: "At Risk", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", bar: "#D97706" };
  return { key: "off_track", label: "Off Track", color: "text-rose-700", bg: "bg-rose-50", border: "border-rose-200", bar: "#DC2626" };
}

/**
 * Generate the per-theme sample KPI list. Achieved values are deterministic:
 * we pick an outcome distribution (60% on-track or better, 25% at-risk, 15% off-track)
 * and the trend arrow is also seeded.
 */
export function fabricateThemeKpis(theme) {
  const tpl = TEMPLATES[theme] || TEMPLATES[String(theme || "").trim()] || FALLBACK;
  const rng = rngFrom(seed32(`kpi::${theme}`));
  return tpl.map((k, i) => {
    const bucket = rng();
    // Distribution: 18% completed, 42% on-track, 25% at-risk, 15% off-track
    let pct;
    if (bucket < 0.18) pct = 100 + Math.floor(rng() * 14); // 100-113
    else if (bucket < 0.60) pct = 75 + Math.floor(rng() * 24); // 75-98
    else if (bucket < 0.85) pct = 55 + Math.floor(rng() * 19); // 55-73
    else pct = 25 + Math.floor(rng() * 24); // 25-48

    const achieved = Math.round((k.target * pct) / 100);
    const trend = rng() > 0.4 ? "up" : "down";
    return {
      id: `${theme}-${i}`,
      name: k.name,
      target: k.target,
      achieved,
      unit: k.unit,
      pct,
      trend,
      status: statusFor(pct),
    };
  });
}

export function summariseKpiStatuses(kpis) {
  const out = { completed: 0, on_track: 0, at_risk: 0, off_track: 0 };
  kpis.forEach((k) => {
    out[k.status.key] += 1;
  });
  return out;
}
